// Cart utility functions

export interface CartItem {
  id: number;
  name: string;
  price: number;
  quantity: number;
  commission: number;
  recurring?: boolean;
  category?: string;
  image: string;
}

// Get cart from localStorage
export const getCart = (): CartItem[] => {
  try {
    const cart = localStorage.getItem('cart');
    return cart ? JSON.parse(cart) : [];
  } catch (error) {
    console.error('Error reading cart:', error);
    return [];
  }
};

// Save cart to localStorage
export const saveCart = (cart: CartItem[]): void => {
  try {
    localStorage.setItem('cart', JSON.stringify(cart));
    // Dispatch custom event for cart updates
    window.dispatchEvent(new CustomEvent('cartUpdated', { detail: cart }));
  } catch (error) {
    console.error('Error saving cart:', error);
  }
};

// Add item to cart
export const addToCart = (item: Omit<CartItem, 'quantity'>): void => {
  const cart = getCart();
  const existingItem = cart.find(i => i.id === item.id);

  if (existingItem) {
    existingItem.quantity += 1;
  } else {
    cart.push({ ...item, quantity: 1 });
  }

  saveCart(cart);
};

// Remove item from cart
export const removeFromCart = (itemId: number): void => {
  const cart = getCart();
  const updatedCart = cart.filter(item => item.id !== itemId);
  saveCart(updatedCart);
};

// Update item quantity
export const updateQuantity = (itemId: number, quantity: number): void => {
  if (quantity <= 0) {
    removeFromCart(itemId);
    return;
  }

  const cart = getCart();
  const item = cart.find(i => i.id === itemId);
  
  if (item) {
    item.quantity = quantity;
    saveCart(cart);
  }
};

// Clear cart
export const clearCart = (): void => {
  localStorage.removeItem('cart');
  window.dispatchEvent(new CustomEvent('cartUpdated', { detail: [] }));
};

// Get cart total
export const getCartTotal = (): number => {
  const cart = getCart();
  return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
};

// Get cart item count
export const getCartItemCount = (): number => {
  const cart = getCart();
  return cart.reduce((sum, item) => sum + item.quantity, 0);
};

// Get total commission
export const getTotalCommission = (): number => {
  const cart = getCart();
  return cart.reduce((sum, item) => sum + item.commission * item.quantity, 0);
};
